import { ENVIRONMENT } from './../environments/environment';
// ====================
// == MODULES IMPORT ==
// ====================
import { NgModule, ErrorHandler, APP_INITIALIZER } from '@angular/core';
import { DecimalPipe } from '@angular/common';
import { Router } from '@angular/router';
import { AngularFireModule } from '@angular/fire';
import {
  AngularFireDatabase,
  AngularFireDatabaseModule,
} from '@angular/fire/database';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { HttpClientModule, HTTP_INTERCEPTORS } from '@angular/common/http';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatSelectModule } from '@angular/material/select';
import { BrowserModule } from '@angular/platform-browser';
import {
  BrowserAnimationsModule,
  NoopAnimationsModule,
} from '@angular/platform-browser/animations';
import { RouterModule } from '@angular/router';
import {
  MissingTranslationHandler,
  MissingTranslationHandlerParams,
  TranslateLoader,
  TranslateModule,
} from '@ngx-translate/core';
import { ChartModule } from 'angular-highcharts';
import { ClipboardModule } from 'ngx-clipboard';
import { DropzoneModule } from 'ngx-dropzone-wrapper';
import {
  SWIPER_CONFIG,
  SwiperModule,
  SwiperConfigInterface,
} from 'ngx-swiper-wrapper';
import { NgxWebstorageModule } from 'ngx-webstorage';
import { Keepalive, NgIdleKeepaliveModule } from '@ng-idle/keepalive';
import { CookieService } from 'ngx-cookie-service';
import { AccordionModule } from 'primeng/accordion';
import { CalendarModule } from 'primeng/calendar';
import { CarouselModule } from 'primeng/carousel';
import { DropdownModule } from 'primeng/dropdown';
import { ProgressBarModule } from 'primeng/progressbar';

import { TwoFaModule } from './components/two-fa/two-fa.module';
import { AppInterceptor } from './app.interceptor';
import { ROUTES } from './app.routing';
import { ModalModule } from './components/modal/modal.module';
import { registerLocaleData } from '@angular/common';
import localeID from '@angular/common/locales/id';

// =====================
// == SERVICES IMPORT ==
// =====================
import { AuthGuard } from './services/auth-guard.service';
import { AuthService } from './services/auth.service';
import { BaseParameterService } from './services/base-parameter.service';
import { MyInfoMappingService } from './services/myinfo-mapping.service';
import { CryptographyService } from './services/cryptography.service';
import { DialogService } from './services/dialog.service';
import { DocumentService } from './services/document.service';
import { FinanceService } from './services/finance.service';
import { LanguageService } from './services/language.service';
import { ReviewAppService } from './services/review-app.service';
import { LoadingService } from './services/loading.service';
import { LoanService } from './services/loan.service';
import { BankIntegrationService } from './services/bank-integration.service';
import { BondService } from './services/bond.service';
import { MemberService } from './services/member.service';
import { PlatformMyInfoService } from './services/platform-my-info.service';
import { MetaService } from './services/meta.service';
import { NoteService } from './services/note.service';
import { NotificationService } from './services/notification.service';
import { ReferralService } from './services/referral.service';
import { SecurityService } from './services/security.service';
import { ScriptInjectorService } from './services/script-injector.service';
import { UserService } from './services/user.service';
import { UtilityService } from './services/utility.service';
import { MathUtilitiesService } from './services/math-utilities.service';
import { ValidatorService } from './services/validator.service';
import { WindowService } from './services/window.service';
import { FormService } from './services/form.service';
import { SsoService } from './services/sso.service';
import { ModalService } from './services/modal.service';
import { MyInfoService } from './services/myinfo.service';
import { MenuService } from './services/menu.service';
import { HeaderService } from './services/header.service';
import { DepositService } from './services/deposit.service';
import { EventService } from './services/event.service';
import { AutoFormatService } from './services/auto-format.service';
import { BaseService } from './services/base.service';
import { TwoFaService } from './services/two-fa.service';
import { CustomPreloading } from './services/preload-strategy.service';
import { FeatureFlagService } from './services/feature-flag.service';
import { MessageService } from './services/message.service';
import { KeepaliveCustom } from './services/keepalive.service';
// =======================
// == COMPONENTS IMPORT ==
// =======================
import { AppComponent } from './app.component';
import { FooterComponent } from './components/footer.component';
import { HeaderComponent } from './components/header.component';
import { LoadingComponent } from './components/loading.component';
import { NotificationComponent } from './components/notification.component';
import { NotFoundPageComponent } from './components/not-found-page.component';
import { MaintenanceComponent } from './components/maintenance/maintenance.component';

// =======================
// == DIRECTIVES IMPORT ==
// =======================

// ====================
// == LOADERS IMPORT ==
// ====================
import { FirebaseTranslationLoader } from './loaders/firebase-translation.loader';
import { RecaptchaModule } from 'ng-recaptcha';

// ===============
// == CONSTANTS ==
// ===============
import CONFIGURATION from '../configurations/configuration';
import { ServiceWorkerModule } from '@angular/service-worker';
import * as Sentry from '@sentry/angular';
import { AuthorizationGuard } from './services/authorization-guard.service';

const Highcharts = require('highcharts');

const firebaseConfig = {
  databaseURL: 'https://alice-writes-web-3.firebaseio.com',
  projectId: 'alice-writes-web-3',
};

Highcharts.setOptions({
  lang: {
    thousandsSep: CONFIGURATION.format.thousands_separator,
    decimalPoint: CONFIGURATION.format.decimal_separator,
  },
});
registerLocaleData(localeID, 'id');
// ===============
// == FACTORIES ==
// ===============
export function firebaseTranslationLoaderFactory(
  angularFireDatabase: AngularFireDatabase
) {
  return new FirebaseTranslationLoader(angularFireDatabase);
}

export class CustomMissingTranslationHandler
  implements MissingTranslationHandler
{
  handle(params: MissingTranslationHandlerParams) {
    return '';
  }
}

export function provideErrorHandler() {
  if (ENVIRONMENT.sentry_logging) {
    return Sentry.createErrorHandler();
  } else {
    return new ErrorHandler();
  }
}

const DEFAULT_SWIPER_CONFIG: SwiperConfigInterface = {
  direction: 'horizontal',
  slidesPerView: 'auto',
};

@NgModule({
  declarations: [
    AppComponent,
    FooterComponent,
    HeaderComponent,
    LoadingComponent,
    NotificationComponent,
    NotFoundPageComponent,
    MaintenanceComponent,
  ],
  imports: [
    AccordionModule,
    AngularFireDatabaseModule,
    AngularFireModule.initializeApp(firebaseConfig),
    BrowserAnimationsModule,
    BrowserModule,
    CalendarModule,
    CarouselModule,
    ChartModule,
    ClipboardModule,
    DropdownModule,
    DropzoneModule,
    FormsModule,
    HttpClientModule,
    MatCheckboxModule,
    MatSelectModule,
    NgxWebstorageModule.forRoot(),
    NgIdleKeepaliveModule.forRoot(),
    NoopAnimationsModule,
    ProgressBarModule,
    ReactiveFormsModule,
    SwiperModule,
    ModalModule,
    TranslateModule.forRoot({
      missingTranslationHandler: {
        provide: MissingTranslationHandler,
        useClass: CustomMissingTranslationHandler,
      },
      loader: {
        provide: TranslateLoader,
        useFactory: firebaseTranslationLoaderFactory,
        deps: [AngularFireDatabase],
      },
    }),
    TwoFaModule,
    RouterModule.forRoot(ROUTES, {
      preloadingStrategy: CustomPreloading,
      relativeLinkResolution: 'legacy',
    }),
    ServiceWorkerModule.register('ngsw-worker.js', {
      enabled: ENVIRONMENT.production,
      // Register the ServiceWorker as soon as the app is stable
      // or after 30 seconds (whichever comes first).
      registrationStrategy: 'registerWhenStable:30000',
    }),
    RecaptchaModule,
  ],
  providers: [
    {
      provide: SWIPER_CONFIG,
      useValue: DEFAULT_SWIPER_CONFIG,
    },
    {
      provide: Keepalive,
      useClass: KeepaliveCustom,
    },
    AuthGuard,
    AuthorizationGuard,
    AuthService,
    BaseParameterService,
    BankIntegrationService,
    BondService,
    MyInfoMappingService,
    CryptographyService,
    DecimalPipe,
    DocumentService,
    DialogService,
    FinanceService,
    LanguageService,
    LoadingService,
    LoanService,
    MathUtilitiesService,
    MemberService,
    MetaService,
    NoteService,
    NotificationService,
    PlatformMyInfoService,
    ReferralService,
    SecurityService,
    ScriptInjectorService,
    UserService,
    SsoService,
    UtilityService,
    ValidatorService,
    WindowService,
    ReviewAppService,
    FormService,
    ModalService,
    MyInfoService,
    MenuService,
    HeaderService,
    DepositService,
    EventService,
    AutoFormatService,
    TwoFaService,
    BaseService,
    FeatureFlagService,
    MessageService,
    CookieService,
    {
      provide: HTTP_INTERCEPTORS,
      useClass: AppInterceptor,
      multi: true,
    },
    { provide: ErrorHandler, useFactory: provideErrorHandler },
    {
      provide: Sentry.TraceService,
      deps: [Router],
    },
    {
      provide: APP_INITIALIZER,
      useFactory: () => () => {},
      deps: [Sentry.TraceService],
      multi: true,
    },
    CustomPreloading,
  ],
  bootstrap: [AppComponent],
})
export class AppModule {}
